'use client';

import { TravelConfig, LegacyLocation, Travel } from '@/types';

// Validation error types
export class ValidationError extends Error {
  constructor(message: string, public field?: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

// Location validator
function validateLocation(location: unknown, fieldName: string): LegacyLocation {
  if (!location || typeof location !== 'object') {
    throw new ValidationError(`${fieldName} must be an object`);
  }

  const loc = location as Record<string, unknown>;

  if (typeof loc.city !== 'string' || !loc.city.trim()) {
    throw new ValidationError(`${fieldName}.city must be a non-empty string`);
  }

  if (typeof loc.country !== 'string' || !loc.country.trim()) {
    throw new ValidationError(`${fieldName}.country must be a non-empty string`);
  }

  if (typeof loc.timezone !== 'string' || !loc.timezone.trim()) {
    throw new ValidationError(`${fieldName}.timezone must be a non-empty string`);
  }

  // Validate timezone format
  const timezoneRegex = /^[A-Za-z_]+\/[A-Za-z_]+$/;
  if (!timezoneRegex.test(loc.timezone)) {
    throw new ValidationError(`${fieldName}.timezone must be in format "Region/City" (e.g., "America/New_York")`);
  }

  return {
    city: loc.city.trim(),
    country: loc.country.trim(),
    timezone: loc.timezone.trim(),
    isTravel: typeof loc.isTravel === 'boolean' ? loc.isTravel : undefined
  };
}

// Date validation
function validateDateString(date: unknown, fieldName: string): string {
  if (typeof date !== 'string') {
    throw new ValidationError(`${fieldName} must be a string`);
  }

  // Validate YYYY-MM-DD format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) {
    throw new ValidationError(`${fieldName} must be in YYYY-MM-DD format`);
  }

  // Validate actual date
  const dateObj = new Date(date + 'T00:00:00.000Z');
  if (isNaN(dateObj.getTime())) {
    throw new ValidationError(`${fieldName} is not a valid date`);
  }

  return date;
}

// Travel validator
function validateTravel(travel: unknown, index: number): Travel {
  if (!travel || typeof travel !== 'object') {
    throw new ValidationError(`travels[${index}] must be an object`);
  }

  const t = travel as Record<string, unknown>;

  if (typeof t.id !== 'string' || !t.id.trim()) {
    throw new ValidationError(`travels[${index}].id must be a non-empty string`);
  }

  const startDate = validateDateString(t.startDate, `travels[${index}].startDate`);
  const endDate = validateDateString(t.endDate, `travels[${index}].endDate`);

  // Validate date order
  if (new Date(startDate) >= new Date(endDate)) {
    throw new ValidationError(`travels[${index}].startDate must be before endDate`);
  }

  const location = validateLocation(t.location, `travels[${index}].location`);

  return {
    id: t.id.trim(),
    startDate,
    endDate,
    location
  };
}

// Main validator
export function validateTravelConfig(data: unknown): TravelConfig {
  if (!data || typeof data !== 'object') {
    throw new ValidationError('Travel config must be an object');
  }

  const config = data as Record<string, unknown>;

  // Validate homeLocation
  const homeLocation = validateLocation(config.homeLocation, 'homeLocation');

  // Validate travels array
  if (!Array.isArray(config.travels)) {
    throw new ValidationError('travels must be an array');
  }

  const travels = config.travels.map((travel, index) => validateTravel(travel, index));

  // Validate no overlapping travels
  for (let i = 0; i < travels.length; i++) {
    for (let j = i + 1; j < travels.length; j++) {
      const travel1 = travels[i];
      const travel2 = travels[j];

      const start1 = new Date(travel1.startDate);
      const end1 = new Date(travel1.endDate);
      const start2 = new Date(travel2.startDate);
      const end2 = new Date(travel2.endDate);

      // Check for overlap
      if (start1 < end2 && start2 < end1) {
        throw new ValidationError(
          `travels[${i}] (${travel1.id}) overlaps with travels[${j}] (${travel2.id})`
        );
      }
    }
  }

  return {
    homeLocation,
    travels
  };
}

// Sanitization helpers
export function sanitizeString(value: string): string {
  return value.trim().replace(/[<>\"'&]/g, '');
}

export function sanitizeLocation(location: LegacyLocation): LegacyLocation {
  return {
    city: sanitizeString(location.city),
    country: sanitizeString(location.country),
    timezone: sanitizeString(location.timezone),
    isTravel: location.isTravel
  };
}

// Safe JSON parser with validation
export function parseTravelConfig(jsonString: string): TravelConfig {
  try {
    const data = JSON.parse(jsonString);
    const validatedConfig = validateTravelConfig(data);

    // Sanitize the validated data
    return {
      homeLocation: sanitizeLocation(validatedConfig.homeLocation),
      travels: validatedConfig.travels.map(travel => ({
        ...travel,
        location: sanitizeLocation(travel.location)
      }))
    };
  } catch (error) {
    if (error instanceof ValidationError) {
      throw error;
    }
    throw new ValidationError('Invalid JSON format');
  }
}
